import { Head, Link } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { FlashNotice } from "@/components/admin/FlashNotice"
import { WeeklyReportsSubNav } from "@/components/weekly_reports/WeeklyReportsSubNav"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { DataRow, DataTable } from "@/components/ui/data-table"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table"
import type { WeeklyReportFull } from "@/types/weekly_report"

export default function WeeklyReportShow({
  report,
  editable,
}: {
  report: WeeklyReportFull
  editable: boolean
}) {
  const title = `${report.person} · ${report.week_label}`
  const description = `The weekly activity report ${report.person} filed for ${report.week_label}.`

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell>
        <PageHeader
          title={report.week_label ?? "Weekly report"}
          description={report.person}
          tabs={<WeeklyReportsSubNav />}
          actions={
            editable ? (
              <Button asChild>
                <Link href={`/weekly_reports/${report.id}/edit`}>Edit this report</Link>
              </Button>
            ) : null
          }
        />

        <div className="mt-6">
          <FlashNotice />

          <DataTable>
            <DataRow title="Filed by">
              {report.person}
              {report.imported && (
                <Badge tone="muted" className="ml-2">
                  From the old system
                </Badge>
              )}
            </DataRow>
            {report.department && <DataRow title="Department">{report.department}</DataRow>}
            {report.position && <DataRow title="Title">{report.position}</DataRow>}
            <DataRow title="Worked in">
              {report.worked_in.length > 0 ? (
                report.worked_in.join(", ")
              ) : report.legacy_worked_in ? (
                /*
                  The old system took this as free text. Where the value named a
                  District Council it is linked above; where it did not — "Home
                  Office", "Remote" — the original words are still what the
                  person wrote, so they are shown rather than dropped.
                */
                <span>{report.legacy_worked_in}</span>
              ) : (
                <span className="text-ink-muted">—</span>
              )}
            </DataRow>
            <DataRow title="Filed">
              {report.submitted_at ? formatStamp(report.submitted_at) : "—"}
            </DataRow>
            {report.edited && report.updated_at && (
              <DataRow title="Last updated">{formatStamp(report.updated_at)}</DataRow>
            )}
          </DataTable>

          <h2 className="mt-10">The week</h2>
          <DataTable className="mt-4">
            {report.questions.map((question) => (
              <DataRow key={question.key} title={question.label}>
                {report.answers[question.key] ? (
                  <p className="whitespace-pre-line">{report.answers[question.key]}</p>
                ) : (
                  <span className="text-ink-muted">Nothing recorded.</span>
                )}
              </DataRow>
            ))}
          </DataTable>

          <h2 className="mt-10">How the time was spent</h2>
          <div className="mt-4 max-w-md">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Category</TableHeaderCell>
                  <TableHeaderCell numeric>Percent</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.lm_categories.map((category) => (
                  <TableRow key={category.key}>
                    <TableCell>{category.label}</TableCell>
                    <TableCell numeric>{report.percentages[category.key] ?? 0}%</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell className="font-medium text-ink-display">Total</TableCell>
                  <TableCell numeric className="font-medium text-ink-display">
                    {report.lm_total}%
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>

          {report.imported && report.lm_total !== 100 && (
            <p className="mt-3 text-sm text-ink-muted">
              Reports filed in the Portal must total 100%. This one came from the
              old system, which did not require it.
            </p>
          )}

          <div className="mt-10">
            <Link href="/weekly_reports">Back to the grid</Link>
          </div>
        </div>
      </AppShell>
    </>
  )
}

function formatStamp(value: string) {
  return new Date(value).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  })
}
